<template>
    <jet-action-section>
        <template #title>
            Delete Account
        </template>

        <template #description>
            Permanently delete your account.
        </template>

        <template #content>
            <div class="max-w-xl text-sm text-gray-600">
                Once your account is deleted, all of its resources and data will be permanently deleted. Before deleting your account, please download any data or information that you wish to retain.
            </div>

            <div class="mt-5">
                <jet-danger-button @click="confirmUserDeletion">
                    Delete Account
                </jet-danger-button>
            </div>

            <!-- Delete Account Confirmation Modal -->
            <jet-dialog-modal :show="confirmingUserDeletion" @close="closeModal">
                <template #title>
                    Delete Account
                </template>

                <template #content>
                    <!-- If user has active/trialing subscriptions -->
                    <div v-if="activeSubscriptions.length" class="">
                        <alert-card :type="'warning'" class="mb-4">
                            <p class="font-semibold mb-2">
                                You currently have the following active subscriptions:
                            </p>
                            <ul class="space-y-2">
                                <li v-for="(sub, idx) in activeSubscriptions" :key="idx"
                                    class="flex items-start justify-between border rounded p-2 bg-white">
                                    <div>
                                        <div class="font-medium capitalize">
                                            {{ sub.type }} subscription
                                            <span class="text-xs ml-2 px-2 py-0.5 rounded bg-gray-100 text-gray-700">
                                                {{ sub.status }} · {{ sub.frequency || '—' }}
                                            </span>
                                        </div>
                                        <div class="text-sm text-gray-600">
                                            Next payment amount: <span class="font-medium">${{ sub.plan_amount?.toFixed(2) }}</span>
                                        </div>
                                        <div class="text-sm text-gray-600">
                                            Next payment date: <span class="font-medium">{{ sub.next_payment_date || '—' }}</span>
                                        </div>
                                    </div>
                                </li>
                            </ul>
                            <p class="mt-3 text-sm">
                                We will cancel these subscriptions before deleting your account.
                            </p>
                        </alert-card>
                    </div>

                    Are you sure you want to delete your account? Once your account is deleted, all of its resources and data will be permanently deleted. Please enter your password to confirm you would like to permanently delete your account.

                    <div class="mt-4">
                        <jet-input type="password" class="mt-1 block w-3/4" placeholder="Password"
                                    ref="password"
                                    v-model="form.password"
                                    @keyup.enter="deleteUser" />

                        <jet-input-error :message="form.errors.password" class="mt-2" />
                    </div>
                    <div v-if="cancelError" class="mt-3 text-sm text-red-600">
                        {{ cancelError }}
                    </div>
                </template>

                <template #footer>
                    <jet-secondary-button @click="closeModal">
                        Cancel
                    </jet-secondary-button>

                    <jet-danger-button class="ml-2" @click="deleteUser" :class="{ 'opacity-25': form.processing }" :disabled="form.processing">
                        Delete Account
                    </jet-danger-button>
                </template>
            </jet-dialog-modal>
        </template>
    </jet-action-section>
</template>

<script>
    import JetActionSection from '@/Jetstream/ActionSection.vue'
    import JetDialogModal from '@/Jetstream/DialogModal.vue'
    import JetDangerButton from '@/Jetstream/DangerButton.vue'
    import JetInput from '@/Jetstream/Input.vue'
    import JetInputError from '@/Jetstream/InputError.vue'
    import JetSecondaryButton from '@/Jetstream/SecondaryButton.vue'
    import axios from 'axios'
    import AlertCard from "@/Shared/AlertCard.vue";

    export default {
        components: {
            JetActionSection,
            JetDangerButton,
            JetDialogModal,
            JetInput,
            JetInputError,
            JetSecondaryButton,
            AlertCard
        },

        data() {
            return {
                confirmingUserDeletion: false,

                form: this.$inertia.form({
                    password: '',
                }),

                activeSubscriptions: [],
                cancelError: null,
                loadingActive: false,
            }
        },

        methods: {
            async confirmUserDeletion() {
                this.confirmingUserDeletion = true
                this.cancelError = null
                this.activeSubscriptions = []

                // Autofocus password a bit later
                setTimeout(() => this.$refs.password?.focus(), 250)

                try {
                    const { data } = await axios.get(route('user.active-subscriptions'))
                    this.activeSubscriptions = Array.isArray(data?.subscriptions) ? data.subscriptions : []
                    // Clear any previous error if we successfully loaded (even if empty)
                    this.cancelError = null
                } catch (e) {
                    // Only show error for actual failures (network, server errors, etc.)
                    // Empty subscriptions should not trigger this
                    if (e.response?.status >= 500 || !e.response) {
                        // Server error or network issue
                        this.cancelError = 'Could not load your subscriptions. You can still proceed.'
                    }
                    // For 4xx errors (auth issues, etc.), silently handle - user can still proceed
                    this.activeSubscriptions = []
                } finally {
                    this.loadingActive = false
                }
            },

            async deleteUser() {
                this.cancelError = null

                // Account deletion now handles subscription cancellation after password verification
                await this.form.delete(route('current-user.destroy'), {
                    preserveScroll: true,
                    onSuccess: () => this.closeModal(),
                    onError: () => this.$refs.password?.focus(),
                    onFinish: () => this.form.reset(),
                })
            },

            closeModal() {
                this.confirmingUserDeletion = false

                this.form.reset()
            },
        },
    }
</script>
